import type { BaseQueryFn } from "@reduxjs/toolkit/query";

import { graphqlBaseQuery } from "./graphqlBaseQuery";
import { updateTokens, logout as clearAuthTokens } from "../slice/authSlice";
import { logout as endLayoutSession } from "../slice/layoutSlice";

interface RotatedTokens {
  accessToken: string;
  refreshToken: string;
}

const GRAPHQL_ENDPOINT =
  (import.meta.env.API_GRAPHQL_ENDPOINT as string | undefined) ??
  "http://localhost:4000/graphql";

const rawBaseQuery = graphqlBaseQuery({ url: GRAPHQL_ENDPOINT });

// Treat an error as "access token expired/invalid" when it mentions the JWT.
// (jsonwebtoken throws "jwt expired" / "jwt malformed" / "jwt must be provided";
// Apollo may also tag it UNAUTHENTICATED.) Login/validation errors won't match.
const isAuthError = (error: unknown): boolean => {
  const e = error as { message?: string; extensions?: { code?: string } };
  if (e?.extensions?.code === "UNAUTHENTICATED") return true;
  const message = (e?.message ?? "").toLowerCase();
  return /jwt|token expired|expired|unauthori|unauthenticated|authentication required|invalid signature|malformed/.test(
    message,
  );
};

// Single-flight: concurrent failures share one refresh request.
let refreshPromise: Promise<RotatedTokens | null> | null = null;

// Exchanges the stored refresh token for a rotated access + refresh pair. The
// server invalidates the old refresh token, so we must persist the new one.
const requestRotatedTokens = async (): Promise<RotatedTokens | null> => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  try {
    const response = await fetch(GRAPHQL_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `
          query RefreshAccessToken($refreshToken: String!) {
            refreshAccessToken(refreshToken: $refreshToken) {
              accessToken
              refreshToken
            }
          }
        `,
        variables: { refreshToken },
      }),
    });

    const json = await response.json();
    const result = json?.data?.refreshAccessToken;

    if (
      json?.errors?.length ||
      typeof result?.accessToken !== "string" ||
      !result.accessToken ||
      typeof result?.refreshToken !== "string" ||
      !result.refreshToken
    ) {
      return null;
    }
    return { accessToken: result.accessToken, refreshToken: result.refreshToken };
  } catch {
    return null;
  }
};

/**
 * Wraps the GraphQL base query with re-auth:
 *  - on an auth error, silently refresh the access token and retry once;
 *  - if the refresh token is missing/expired, force a logout (back to Login).
 */
export const baseQueryWithReauth: BaseQueryFn = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (!result.error || !isAuthError(result.error)) {
    return result;
  }

  if (!refreshPromise) {
    refreshPromise = requestRotatedTokens().finally(() => {
      refreshPromise = null;
    });
  }
  const rotated = await refreshPromise;

  if (rotated) {
    // graphqlBaseQuery reads the token from localStorage, so update both there
    // (the old refresh token is now dead server-side).
    localStorage.setItem("accessToken", rotated.accessToken);
    localStorage.setItem("refreshToken", rotated.refreshToken);
    api.dispatch(updateTokens(rotated));
    result = await rawBaseQuery(args, api, extraOptions);
  } else {
    // No valid refresh token → end the session and return to the Login screen.
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("sessionToken");
    api.dispatch(clearAuthTokens());
    api.dispatch(endLayoutSession());
  }

  return result;
};
